import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ChevronRight, ChevronLeft, Users, Send, Calendar, Rocket, Sparkles, Loader2, Coins, ShieldCheck,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import CreditUsageWidget from "@/components/CreditUsageWidget";
import { SandboxBanner } from "@/components/outreach/shared";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CREDITS_PER_EMAIL = 1;

function StepDots({ step }) {
    return (
        <div className="flex items-center gap-2 mb-6">
            {[1, 2, 3, 4].map((n) => (
                <React.Fragment key={n}>
                    <span className={`h-6 w-6 rounded-full grid place-items-center text-[10px] font-semibold ${n <= step ? "gradient-bg-cta text-white" : "bg-[var(--color-panel-ai)] text-text-secondary border border-[var(--color-border)]"}`}>{n}</span>
                    {n < 4 && <span className="w-6 h-px bg-[var(--color-border)]" />}
                </React.Fragment>
            ))}
        </div>
    );
}

export default function OutreachCampaignNew() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // wizard state
    const [sequences, setSequences] = useState([]);
    const [selectedSequenceId, setSelectedSequenceId] = useState("");
    const [savedLeads, setSavedLeads] = useState([]);
    const [selectedLeadIds, setSelectedLeadIds] = useState(new Set());
    const [inline, setInline] = useState([]);
    const [meta, setMeta] = useState(null);
    const [senderEmail, setSenderEmail] = useState("");
    const [senderName, setSenderName] = useState("Marco @ Global Lead Suite");
    const [scheduleNow, setScheduleNow] = useState(true);
    const [scheduledAt, setScheduledAt] = useState("");
    const [campaignName, setCampaignName] = useState("");
    const [launching, setLaunching] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [seqs, leadsData, m] = await Promise.all([
                    api.outreachSequences(),
                    api.leadsSaved(200, 0),
                    api.outreachMeta(),
                ]);
                setSequences(seqs.items || []);
                setSavedLeads(leadsData.items || []);
                setMeta(m);
                setSenderEmail(m.sender_email_default || "");
            } catch (e) {
                toast.error(e.message || "Setup load failed");
            }
        })();
    }, []);

    const toggleLead = (id) => {
        setSelectedLeadIds((cur) => {
            const next = new Set(cur);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const parseCsv = (text) => {
        const lines = text.trim().split(/\r?\n/);
        if (lines.length < 2) { toast.error("CSV must have header + at least 1 row"); return; }
        const cols = lines[0].split(",").map(s => s.trim().toLowerCase());
        const emailIdx = cols.indexOf("email");
        if (emailIdx === -1) { toast.error("CSV missing 'email' column"); return; }
        const fnIdx = cols.indexOf("first_name");
        const lnIdx = cols.indexOf("last_name");
        const coIdx = cols.indexOf("company");
        const jtIdx = cols.indexOf("job_title");
        const rows = [];
        for (let i = 1; i < lines.length; i++) {
            const parts = lines[i].split(",").map(s => s.trim());
            const email = parts[emailIdx];
            if (!email || !email.includes("@")) continue;
            rows.push({
                email,
                first_name: fnIdx >= 0 ? parts[fnIdx] || "" : "",
                last_name: lnIdx >= 0 ? parts[lnIdx] || "" : "",
                company: coIdx >= 0 ? parts[coIdx] || "" : "",
                job_title: jtIdx >= 0 ? parts[jtIdx] || "" : "",
            });
        }
        setInline(rows);
        toast.success(`${rows.length} recipients parsed`);
    };

    const totalRecipients = selectedLeadIds.size + inline.length;
    const estimatedCredits = totalRecipients * CREDITS_PER_EMAIL;
    const canLaunch =
        selectedSequenceId && (totalRecipients > 0) && senderEmail && campaignName;
    const insufficient = (user?.credits ?? 0) < estimatedCredits;

    const launch = async () => {
        if (!canLaunch) return;
        if (insufficient) { toast.error("Insufficient credits"); return; }
        setLaunching(true);
        try {
            const { campaign } = await api.outreachCampaignCreate({
                sequence_id: selectedSequenceId,
                name: campaignName,
                sender_email: senderEmail,
                sender_name: senderName,
                scheduled_at: scheduleNow ? null : new Date(scheduledAt).toISOString(),
            });
            // Import recipients
            await api.outreachRecipientsImport(campaign.id, {
                lead_ids: Array.from(selectedLeadIds),
                inline,
            });
            // Launch
            await api.outreachCampaignLaunch(campaign.id);
            await refresh();
            toast.success(`Campaign launched · ~${estimatedCredits} credits`);
            navigate(`/outreach/campaigns/${campaign.id}`);
        } catch (e) {
            if (e.status === 402) toast.error("Insufficient credits");
            else if (e.status === 429) toast.error("Hourly credit cap reached");
            else toast.error(e.message || "Launch failed");
            setLaunching(false);
        }
    };

    if (!user) return null;

    return (
        <div className="min-h-screen bg-[var(--color-bg)] text-text-main flex">
            <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(c => !c)} />
            <div className="flex-1 min-w-0 flex flex-col">
                <Header />
                <main data-testid="campaign-wizard-main" className="flex-1 px-4 md:px-6 py-6 max-w-4xl w-full">
                    <SandboxBanner show={meta?.sandbox} />
                    <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
                        <div>
                            <p className="text-[11px] uppercase tracking-widest text-text-secondary">New campaign</p>
                            <h1 className="mt-1 text-2xl md:text-3xl font-semibold tracking-tight">
                                4-step{" "}
                                <span className="gradient-text">wizard</span>
                            </h1>
                        </div>
                        <CreditUsageWidget />
                    </div>

                    <StepDots step={step} />

                    <motion.div
                        key={step}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                        className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5"
                    >
                        {step === 1 && (
                            <div data-testid="wizard-step-1">
                                <h2 className="text-base font-semibold flex items-center gap-2">
                                    <Sparkles className="h-4 w-4 text-[var(--color-secondary)]" />
                                    Choose a sequence
                                </h2>
                                <p className="text-xs text-text-secondary mt-1">
                                    Select which multi-step sequence to send.
                                </p>
                                <div className="mt-4 space-y-2 max-h-[300px] overflow-y-auto">
                                    {sequences.length === 0 && (
                                        <div className="text-sm text-text-secondary">
                                            No sequences yet. <Link to="/outreach/sequences" className="text-[var(--color-secondary)] hover:underline">Create one first</Link>.
                                        </div>
                                    )}
                                    {sequences.map((s, i) => (
                                        <button
                                            key={s.id}
                                            data-testid={`wizard-sequence-${i}`}
                                            onClick={() => setSelectedSequenceId(s.id)}
                                            className={`w-full text-left rounded-xl border p-3 transition-colors ${selectedSequenceId === s.id ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10" : "border-[var(--color-border)] bg-[var(--color-bg)] hover:border-[var(--color-primary)]/40"}`}
                                        >
                                            <p className="text-sm font-medium">{s.name}</p>
                                            <p className="mt-1 text-xs text-text-secondary">
                                                {s.steps?.length || 0} step · {s.description || "no description"}
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {step === 2 && (
                            <div data-testid="wizard-step-2">
                                <h2 className="text-base font-semibold flex items-center gap-2">
                                    <Users className="h-4 w-4 text-[var(--color-secondary)]" />
                                    Recipients
                                </h2>
                                <p className="text-xs text-text-secondary mt-1">
                                    Pick saved leads (checkbox) or paste a CSV.
                                </p>

                                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-[11px] uppercase tracking-widest text-text-secondary/80 mb-2">
                                            Saved leads · {selectedLeadIds.size}/{savedLeads.length}
                                        </p>
                                        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] max-h-[260px] overflow-y-auto">
                                            {savedLeads.length === 0 ? (
                                                <div className="p-4 text-xs text-text-secondary">
                                                    No saved leads. Bookmark some in <Link to="/leads" className="text-[var(--color-secondary)] hover:underline">Lead Engine</Link>.
                                                </div>
                                            ) : (
                                                savedLeads.map((l, i) => (
                                                    <label
                                                        key={l.id}
                                                        data-testid={`wizard-lead-${i}`}
                                                        className="flex items-center gap-2 px-3 py-2 border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-panel-ai)]/50 cursor-pointer"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedLeadIds.has(l.id)}
                                                            onChange={() => toggleLead(l.id)}
                                                            className="accent-[var(--color-primary)]"
                                                        />
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-sm truncate">{l.full_name}</p>
                                                            <p className="text-[11px] text-text-secondary truncate">{l.email} · {l.company}</p>
                                                        </div>
                                                    </label>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-[11px] uppercase tracking-widest text-text-secondary/80 mb-2">
                                            Or paste CSV (header row required)
                                        </p>
                                        <textarea
                                            data-testid="wizard-csv-input"
                                            placeholder="email,first_name,last_name,company,job_title
                                            
alice@acme.com,Alice,Rossi,Acme,CEO
bob@nova.io,Bob,Verdi,Nova,CTO"
                                            rows={10}
                                            onChange={(e) => parseCsv(e.target.value)}
                                            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-xs font-mono focus:outline-none focus:border-[var(--color-primary)]/60"
                                        />
                                        <p className="mt-1 text-[10px] text-text-secondary">
                                            Inline recipients: {inline.length}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div data-testid="wizard-step-3">
                                <h2 className="text-base font-semibold flex items-center gap-2">
                                    <Send className="h-4 w-4 text-[var(--color-secondary)]" />
                                    Sender identity
                                </h2>
                                <div className="mt-4 space-y-3">
                                    <div>
                                        <label className="text-[11px] uppercase tracking-widest text-text-secondary/80">Campaign name</label>
                                        <input
                                            data-testid="wizard-campaign-name"
                                            value={campaignName} onChange={(e) => setCampaignName(e.target.value)}
                                            placeholder="e.g. SaaS Italy · Feb outreach"
                                            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--color-primary)]/60"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[11px] uppercase tracking-widest text-text-secondary/80">Sender email</label>
                                        <select
                                            data-testid="wizard-sender-email"
                                            value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)}
                                            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:outline-none"
                                        >
                                            {meta?.sender_email_default && (
                                                <option value={meta.sender_email_default}>{meta.sender_email_default}</option>
                                            )}
                                        </select>
                                        <p className="mt-1 text-[10px] text-text-secondary flex items-center gap-1">
                                            <ShieldCheck className="h-3 w-3" />
                                            In sandbox mode, ogni email verificata è utilizzabile.
                                        </p>
                                    </div>
                                    <div>
                                        <label className="text-[11px] uppercase tracking-widest text-text-secondary/80">Sender name</label>
                                        <input
                                            data-testid="wizard-sender-name"
                                            value={senderName} onChange={(e) => setSenderName(e.target.value)}
                                            className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--color-primary)]/60"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 4 && (
                            <div data-testid="wizard-step-4">
                                <h2 className="text-base font-semibold flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-[var(--color-secondary)]" />
                                    Schedule & review
                                </h2>
                                <div className="mt-4 space-y-3">
                                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={scheduleNow} onChange={() => setScheduleNow(true)}
                                            className="accent-[var(--color-primary)]"
                                        />
                                        Send now
                                    </label>
                                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={!scheduleNow} onChange={() => setScheduleNow(false)}
                                            className="accent-[var(--color-primary)]"
                                        />
                                        Schedule for
                                    </label>
                                    {!scheduleNow && (
                                        <input
                                            data-testid="wizard-schedule-at"
                                            type="datetime-local"
                                            value={scheduledAt}
                                            onChange={(e) => setScheduledAt(e.target.value)}
                                            className="ml-6 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-1.5 text-sm focus:outline-none"
                                        />
                                    )}

                                    <div className="mt-4 rounded-xl bg-[var(--color-panel-ai)] border border-[var(--color-border)] p-4 space-y-2">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-text-secondary">Recipients</span>
                                            <span data-testid="wizard-review-recipients" className="font-medium tabular-nums">{totalRecipients}</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-text-secondary">Cost per email</span>
                                            <span className="font-medium tabular-nums">{CREDITS_PER_EMAIL} credit</span>
                                        </div>
                                        <div className="flex items-center justify-between text-sm border-t border-[var(--color-border)] pt-2">
                                            <span className="text-text-secondary">Estimated total</span>
                                            <span
                                                data-testid="wizard-review-cost"
                                                className="inline-flex items-center gap-1 font-semibold tabular-nums"
                                            >
                                                <Coins className="h-3.5 w-3.5 text-[var(--color-secondary)]" />
                                                {estimatedCredits} credits
                                            </span>
                                        </div>
                                    </div>

                                    <div className="rounded-xl border border-dashed border-[var(--color-border)] p-4">
                                        <p className="text-[10px] uppercase tracking-widest text-text-secondary">Compliance</p>
                                        <ul className="mt-2 text-xs text-text-secondary space-y-1">
                                            <li className="flex items-center gap-1.5"><ShieldCheck className="h-3 w-3 text-[var(--color-success)]" /> Unsubscribe link auto-inserito in ogni email</li>
                                            <li className="flex items-center gap-1.5"><ShieldCheck className="h-3 w-3 text-[var(--color-success)]" /> List-Unsubscribe header RFC 8058</li>
                                            <li className="flex items-center gap-1.5"><ShieldCheck className="h-3 w-3 text-[var(--color-success)]" /> Warm-up automatico sender domain nuovi</li>
                                            <li className="flex items-center gap-1.5"><ShieldCheck className="h-3 w-3 text-[var(--color-success)]" /> Rate limit 50/h per dominio destinatario</li>
                                        </ul>
                                    </div>

                                    {insufficient && (
                                        <p className="text-xs text-[var(--color-alert)]">
                                            Insufficient credits ({user.credits} available)
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </motion.div>

                    <div className="mt-6 flex justify-between">
                        <button
                            data-testid="wizard-prev"
                            onClick={() => setStep(s => Math.max(1, s - 1))}
                            disabled={step === 1}
                            className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-4 py-2 text-sm text-text-secondary hover:text-text-main disabled:opacity-40"
                        >
                            <ChevronLeft className="h-4 w-4" /> Back
                        </button>
                        {step < 4 ? (
                            <button
                                data-testid="wizard-next"
                                onClick={() => setStep(s => Math.min(4, s + 1))}
                                disabled={
                                    (step === 1 && !selectedSequenceId) ||
                                    (step === 2 && totalRecipients === 0) ||
                                    (step === 3 && (!senderEmail || !campaignName))
                                }
                                className="inline-flex items-center gap-2 rounded-full gradient-bg-cta px-5 py-2 text-sm font-medium text-white disabled:opacity-50"
                            >
                                Next <ChevronRight className="h-4 w-4" />
                            </button>
                        ) : (
                            <button
                                data-testid="wizard-launch"
                                onClick={launch}
                                disabled={!canLaunch || insufficient || launching}
                                className="inline-flex items-center gap-2 rounded-full gradient-bg-cta px-6 py-2.5 text-sm font-medium text-white disabled:opacity-50"
                            >
                                {launching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                                Launch campaign
                            </button>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
}
